import { useEffect, useState } from 'react';
import {
  Package,
  Palette,
  Image,
  Loader2,
  AlertCircle,
  Search,
  ExternalLink,
  ChevronRight,
  Layers,
  Type,
  Droplets,
} from 'lucide-react';
import { clsx } from 'clsx';
import { assetPipelineApi, type BrandToken } from '@/services/api';

export function AssetPipelinePage() {
  const [tokens, setTokens] = useState<BrandToken[]>([]);
  const [selectedBrand, setSelectedBrand] = useState<BrandToken | null>(null);
  const [palette, setPalette] = useState<{
    colors: Record<string, string>;
    fonts: Record<string, string>;
    spacing: Record<string, string>;
  } | null>(null);
  const [assetId, setAssetId] = useState('');
  const [assetResult, setAssetResult] = useState<Record<string, unknown> | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Load brand tokens on mount
  useEffect(() => {
    setLoading(true);
    assetPipelineApi
      .listBrandTokens()
      .then(({ tokens: t }) => setTokens(t))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const handleSelectBrand = async (brand: BrandToken) => {
    setSelectedBrand(brand);
    setPalette(null);
    try {
      const p = await assetPipelineApi.getBrandPalette(brand.brand_id);
      setPalette(p);
    } catch {
      // Palette might not be available
    }
  };

  const handleAssetLookup = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!assetId.trim()) return;
    setError(null);
    setAssetResult(null);
    setLoading(true);
    try {
      const result = await assetPipelineApi.getAsset(assetId.trim());
      setAssetResult(result as unknown as Record<string, unknown>);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Asset not found');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-8">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-navy-900">Asset Pipeline</h1>
        <p className="text-sm text-slate-500 mt-1">
          Browse brand design tokens, look up generated assets, and inspect creative output.
        </p>
      </div>

      {error && (
        <div className="bg-amber-50 border border-amber-200 text-amber-700 text-sm rounded-xl px-4 py-3 flex items-center gap-2">
          <AlertCircle size={16} />
          <span>{error}</span>
        </div>
      )}

      {/* Asset Lookup */}
      <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-6">
        <h2 className="font-semibold text-navy-900 text-sm mb-4 flex items-center gap-2">
          <Search size={16} className="text-brand-teal" />
          Asset Lookup
        </h2>
        <form onSubmit={handleAssetLookup} className="flex gap-3">
          <div className="flex-1 relative">
            <Image size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <input
              type="text"
              value={assetId}
              onChange={(e) => setAssetId(e.target.value)}
              placeholder="Enter asset ID..."
              className="w-full pl-9 pr-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-brand-teal/30 focus:border-brand-teal"
            />
          </div>
          <button
            type="submit"
            disabled={loading || !assetId.trim()}
            className="inline-flex items-center gap-2 px-4 py-2 bg-brand-teal text-navy-900 font-semibold text-sm rounded-lg hover:bg-brand-teal/90 disabled:opacity-40 transition-colors"
          >
            {loading ? <Loader2 size={16} className="animate-spin" /> : <Search size={16} />}
            Lookup
          </button>
        </form>

        {assetResult && (
          <div className="mt-4 bg-slate-50 rounded-lg border border-slate-100 p-4">
            <pre className="text-xs text-slate-700 font-mono overflow-x-auto max-h-48 overflow-y-auto">
              {JSON.stringify(assetResult, null, 2)}
            </pre>
          </div>
        )}
      </div>

      {/* Brand Tokens */}
      <div className="space-y-4">
        <div className="flex items-center gap-3">
          <div className="w-8 h-8 rounded-lg bg-brand-purple/10 flex items-center justify-center text-brand-purple border border-brand-purple/20">
            <Palette size={14} />
          </div>
          <h2 className="text-sm font-bold text-navy-900 uppercase tracking-wide">
            Brand Design Tokens
            {tokens.length > 0 && (
              <span className="ml-2 text-xs text-slate-400 font-normal">({tokens.length})</span>
            )}
          </h2>
        </div>

        {loading && tokens.length === 0 ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 size={24} className="animate-spin text-slate-400" />
          </div>
        ) : tokens.length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {tokens.map((token) => (
              <BrandTokenCard
                key={token.brand_id}
                token={token}
                selected={selectedBrand?.brand_id === token.brand_id}
                onClick={() => handleSelectBrand(token)}
              />
            ))}
          </div>
        ) : (
          <div className="bg-white rounded-xl border border-slate-200 p-8 text-center shadow-sm">
            <Palette size={32} className="mx-auto text-slate-300 mb-3" />
            <p className="text-sm text-slate-400">No brand tokens available</p>
            <p className="text-[11px] text-slate-400 mt-1">
              Brand tokens are created when clients are onboarded with design systems.
            </p>
          </div>
        )}
      </div>

      {/* Selected Brand Palette */}
      {selectedBrand && (
        <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-6">
          <div className="flex items-center gap-3 mb-4">
            <Palette size={16} className="text-brand-teal" />
            <h2 className="font-semibold text-navy-900 text-sm">
              {selectedBrand.brand_name ?? selectedBrand.brand_id} — Design System
            </h2>
          </div>

          {palette ? (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {/* Colors */}
              <div>
                <h3 className="text-xs font-semibold text-navy-900 mb-3 flex items-center gap-1.5">
                  <Droplets size={12} className="text-slate-400" />
                  Colors
                </h3>
                <div className="space-y-2">
                  {Object.entries(palette.colors).map(([name, value]) => (
                    <div key={name} className="flex items-center gap-3">
                      <div
                        className="w-8 h-8 rounded-lg border border-slate-200 flex-shrink-0 shadow-sm"
                        style={{ backgroundColor: value }}
                      />
                      <div>
                        <p className="text-xs font-medium text-navy-900">{name}</p>
                        <p className="text-[10px] font-mono text-slate-400">{value}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Fonts */}
              <div>
                <h3 className="text-xs font-semibold text-navy-900 mb-3 flex items-center gap-1.5">
                  <Type size={12} className="text-slate-400" />
                  Fonts
                </h3>
                <div className="space-y-2">
                  {Object.entries(palette.fonts).map(([role, family]) => (
                    <div key={role} className="bg-slate-50 rounded-lg border border-slate-100 p-3">
                      <p className="text-[10px] text-slate-400 uppercase font-bold">{role}</p>
                      <p className="text-sm text-navy-900 font-medium mt-0.5">{family}</p>
                    </div>
                  ))}
                </div>
              </div>

              {/* Spacing */}
              <div>
                <h3 className="text-xs font-semibold text-navy-900 mb-3 flex items-center gap-1.5">
                  <Layers size={12} className="text-slate-400" />
                  Spacing
                </h3>
                <div className="space-y-1.5">
                  {Object.entries(palette.spacing).map(([name, value]) => (
                    <div key={name} className="flex items-center justify-between py-1.5 border-b border-slate-100 last:border-0">
                      <span className="text-xs text-slate-500">{name}</span>
                      <span className="text-xs font-mono text-navy-900">{value}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          ) : (
            <div className="space-y-4">
              {/* Show inline colors/fonts from token itself */}
              {selectedBrand.colors && Object.keys(selectedBrand.colors).length > 0 && (
                <div>
                  <h3 className="text-xs text-slate-500 mb-2">Colors</h3>
                  <div className="flex flex-wrap gap-2">
                    {Object.entries(selectedBrand.colors).map(([name, value]) => (
                      <div key={name} className="flex items-center gap-2 bg-slate-50 rounded-lg px-3 py-1.5 border border-slate-100">
                        <div className="w-4 h-4 rounded border border-slate-200" style={{ backgroundColor: value }} />
                        <span className="text-[10px] text-slate-600">{name}</span>
                        <span className="text-[10px] font-mono text-slate-400">{value}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              {selectedBrand.fonts && Object.keys(selectedBrand.fonts).length > 0 && (
                <div>
                  <h3 className="text-xs text-slate-500 mb-2">Fonts</h3>
                  <div className="flex flex-wrap gap-2">
                    {Object.entries(selectedBrand.fonts).map(([role, family]) => (
                      <span key={role} className="text-[10px] bg-slate-100 text-slate-600 px-2 py-0.5 rounded-full border border-slate-200">
                        {role}: {family}
                      </span>
                    ))}
                  </div>
                </div>
              )}
              {!selectedBrand.colors && !selectedBrand.fonts && (
                <p className="text-sm text-slate-400 text-center py-4">No detailed palette data available</p>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Brand Token Card
// ---------------------------------------------------------------------------

function BrandTokenCard({
  token,
  selected,
  onClick,
}: {
  token: BrandToken;
  selected: boolean;
  onClick: () => void;
}) {
  const colors = token.colors ? Object.values(token.colors).slice(0, 5) : [];

  return (
    <div
      className={clsx(
        'bg-white rounded-xl border shadow-sm p-4 cursor-pointer transition-all',
        selected
          ? 'border-brand-teal shadow-soft ring-1 ring-brand-teal/20'
          : 'border-slate-200 hover:border-brand-teal/30 hover:shadow-soft',
      )}
      onClick={onClick}
    >
      <div className="flex items-start justify-between mb-3">
        <div>
          <h3 className="text-sm font-bold text-navy-900">{token.brand_name ?? token.brand_id}</h3>
          <p className="text-[10px] font-mono text-slate-400">{token.brand_id}</p>
        </div>
        <ChevronRight size={14} className={clsx('text-slate-300', selected && 'text-brand-teal')} />
      </div>

      {/* Color swatches preview */}
      {colors.length > 0 && (
        <div className="flex gap-1 mt-2">
          {colors.map((color, i) => (
            <div
              key={i}
              className="w-6 h-6 rounded border border-slate-200"
              style={{ backgroundColor: color }}
            />
          ))}
        </div>
      )}

      {token.org_uid && (
        <p className="text-[10px] text-slate-400 mt-2">
          Org: <span className="font-mono">{token.org_uid}</span>
        </p>
      )}
    </div>
  );
}
